import { generateBulkTestCases, getBulkTestCaseGenerationJob } from './api';

import type {
  BulkTestCaseGenerationJobResponse,
  BulkTestCaseItemStatus,
  BulkTestCaseJobStatus,
  GeneratedTestCaseRow,
  RequirementForTestCaseGeneration,
  RequirementGenerationStatus,
} from './types';

export const BATCH_SIZE_OPTIONS = [1, 2, 5, 10, 15] as const;

/*
 * Bulk generation is asynchronous. The frontend periodically requests the
 * current job status instead of keeping the original POST request open.
 */
export const BULK_JOB_POLL_INTERVAL_MS = 10_000;

const MAX_CONSECUTIVE_POLL_ERRORS = 3;

const bulkJobStorageKeyPrefix = 'ses-bulk-test-case-generation-job';

export interface BulkGenerationSummary {
  jobId: string;
  status: BulkTestCaseJobStatus;
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  pending: number;
  persisted: number;
  persistenceFailed: number;
  errorMessage: string;
}

export interface BulkGenerationOutcome {
  summary: BulkGenerationSummary;

  generatedTestCases: GeneratedTestCaseRow[];

  statuses: Record<string, RequirementGenerationStatus>;

  failureMessages: Record<string, string>;
}

export interface BulkGenerationProgress {
  summary: BulkGenerationSummary;

  generatedTestCases: GeneratedTestCaseRow[];

  statuses: Record<string, RequirementGenerationStatus>;

  failureMessages: Record<string, string>;
}

export interface ActiveBulkGenerationJob {
  jobId: string;
  projectId: number;
  requirementThreadIds: string[];
  batchSize: number;
}

interface RunBulkTestCaseGenerationInput {
  projectId: string | number;
  requirements: RequirementForTestCaseGeneration[];
  batchSize: number;
  onProgress?: (progress: BulkGenerationProgress) => void;
  signal?: AbortSignal;
}

interface ResumeBulkTestCaseGenerationInput {
  projectId: string | number;
  onProgress?: (progress: BulkGenerationProgress) => void;
  signal?: AbortSignal;
}

function getBulkJobStorageKey(projectId: number): string {
  return `${bulkJobStorageKeyPrefix}:${projectId}`;
}

function saveActiveBulkGenerationJob(job: ActiveBulkGenerationJob): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      getBulkJobStorageKey(job.projectId),
      JSON.stringify(job),
    );
  } catch {
    /*
     * Persistence is only used to resume polling after a page reload.
     * A localStorage failure must not stop an already submitted job.
     */
  }
}

export function getActiveBulkGenerationJob(
  projectId: number,
): ActiveBulkGenerationJob | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const storedValue = window.localStorage.getItem(
      getBulkJobStorageKey(projectId),
    );

    if (!storedValue) {
      return undefined;
    }

    const job = JSON.parse(storedValue) as ActiveBulkGenerationJob;

    if (
      job.projectId !== projectId ||
      !job.jobId ||
      !Array.isArray(job.requirementThreadIds) ||
      job.requirementThreadIds.length === 0
    ) {
      return undefined;
    }

    return job;
  } catch {
    return undefined;
  }
}

export function clearActiveBulkGenerationJob(projectId: number): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(getBulkJobStorageKey(projectId));
  } catch {
    /*
     * A storage cleanup failure does not change the completed
     * backend job.
     */
  }
}

function isTerminalJobStatus(status: BulkTestCaseJobStatus): boolean {
  return (
    status === 'COMPLETED' ||
    status === 'PARTIALLY_FAILED' ||
    status === 'FAILED'
  );
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

function isJobNotFoundError(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.message.includes('Bulk generation status load failed: 404')
  );
}

function delay(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      const error = new Error('Bulk generation polling was aborted.');

      error.name = 'AbortError';
      reject(error);

      return;
    }

    const timeoutId = window.setTimeout(() => {
      resolve();
    }, milliseconds);

    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timeoutId);

        const error = new Error('Bulk generation polling was aborted.');

        error.name = 'AbortError';
        reject(error);
      },
      { once: true },
    );
  });
}

function itemStatusToGenerationStatus(
  status: BulkTestCaseItemStatus,
  generationSucceeded: boolean,
  errorMessage: string,
): RequirementGenerationStatus {
  switch (status) {
    case 'PENDING':
      return 'PENDING';

    case 'RUNNING':
      return 'GENERATING';

    case 'SUCCEEDED':
      return 'GENERATED';

    case 'FAILED':
      return 'FAILED';

    default:
      /*
       * Preserve compatibility if a gateway returns an unspecified
       * status but the result already contains success/failure data.
       */
      if (generationSucceeded) {
        return 'GENERATED';
      }

      if (errorMessage) {
        return 'FAILED';
      }

      return 'PENDING';
  }
}

export function mergeGeneratedTestCases(
  current: GeneratedTestCaseRow[],
  incoming: GeneratedTestCaseRow[],
): GeneratedTestCaseRow[] {
  const testCaseMap = new Map<string, GeneratedTestCaseRow>();

  current.forEach(testCase => {
    testCaseMap.set(testCase.id, testCase);
  });

  incoming.forEach(testCase => {
    testCaseMap.set(testCase.id, testCase);
  });

  return Array.from(testCaseMap.values());
}

export function createBulkPendingStatuses(
  requirements: RequirementForTestCaseGeneration[],
): Record<string, RequirementGenerationStatus> {
  return requirements.reduce<Record<string, RequirementGenerationStatus>>(
    (statuses, requirement) => {
      statuses[requirement.requirementThreadId] = 'PENDING';

      return statuses;
    },
    {},
  );
}

export function createBulkPendingStatusesFromIds(
  requirementThreadIds: string[],
): Record<string, RequirementGenerationStatus> {
  return requirementThreadIds.reduce<
    Record<string, RequirementGenerationStatus>
  >((statuses, requirementThreadId) => {
    statuses[requirementThreadId] = 'PENDING';

    return statuses;
  }, {});
}

export function createBulkFailedStatuses(
  requirements: RequirementForTestCaseGeneration[],
): Record<string, RequirementGenerationStatus> {
  return requirements.reduce<Record<string, RequirementGenerationStatus>>(
    (statuses, requirement) => {
      statuses[requirement.requirementThreadId] = 'FAILED';

      return statuses;
    },
    {},
  );
}

function mapJobToProgress(
  job: BulkTestCaseGenerationJobResponse,
  requirementThreadIds: string[],
): BulkGenerationProgress {
  const statuses = createBulkPendingStatusesFromIds(requirementThreadIds);

  const failureMessages: Record<string, string> = {};

  let generatedTestCases: GeneratedTestCaseRow[] = [];

  const resultsByRequirementId = new Map(
    job.results.map(result => [result.requirementThreadId, result]),
  );

  requirementThreadIds.forEach(requirementThreadId => {
    const result = resultsByRequirementId.get(requirementThreadId);

    if (!result) {
      /*
       * While the job is running, backend results may only contain
       * items that have started or completed.
       */
      if (isTerminalJobStatus(job.status)) {
        statuses[requirementThreadId] = 'FAILED';

        failureMessages[requirementThreadId] =
          job.errorMessage ||
          'The completed bulk job did not return a result for this requirement.';
      }

      return;
    }

    statuses[requirementThreadId] = itemStatusToGenerationStatus(
      result.status,
      result.generationSucceeded,
      result.errorMessage,
    );

    if (statuses[requirementThreadId] === 'FAILED') {
      failureMessages[requirementThreadId] =
        result.errorMessage ||
        result.errorCode ||
        'Test case generation failed.';
    }

    generatedTestCases = mergeGeneratedTestCases(
      generatedTestCases,
      result.testCases,
    );
  });

  const total = job.total > 0 ? job.total : requirementThreadIds.length;

  const processedFromCounts = job.generationSucceeded + job.generationFailed;

  const processed = isTerminalJobStatus(job.status)
    ? total
    : Math.min(total, processedFromCounts);

  return {
    summary: {
      jobId: job.jobId,
      status: job.status,
      total,
      processed,
      succeeded: job.generationSucceeded,
      failed: job.generationFailed,
      pending: job.pending,
      persisted: job.persisted,
      persistenceFailed: job.persistenceFailed,
      errorMessage: job.errorMessage,
    },

    generatedTestCases,

    statuses,

    failureMessages,
  };
}

async function pollBulkGenerationJob(
  activeJob: ActiveBulkGenerationJob,
  onProgress?: (progress: BulkGenerationProgress) => void,
  signal?: AbortSignal,
): Promise<BulkGenerationOutcome> {
  let consecutivePollErrors = 0;

  for (;;) {
    let job: BulkTestCaseGenerationJobResponse;

    try {
      job = await getBulkTestCaseGenerationJob(
        activeJob.projectId,
        activeJob.jobId,
        signal,
      );

      consecutivePollErrors = 0;
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }

      /*
       * A 404 means this job is no longer resumable for the
       * current project.
       */
      if (isJobNotFoundError(error)) {
        clearActiveBulkGenerationJob(activeJob.projectId);

        throw error;
      }

      consecutivePollErrors += 1;

      if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        /*
         * Keep the active job in localStorage. The backend job may
         * still be running, and polling can resume after retry/reload.
         */
        throw error;
      }

      await delay(BULK_JOB_POLL_INTERVAL_MS, signal);

      continue;
    }

    const progress = mapJobToProgress(job, activeJob.requirementThreadIds);

    onProgress?.(progress);

    if (isTerminalJobStatus(job.status)) {
      clearActiveBulkGenerationJob(activeJob.projectId);

      return {
        summary: progress.summary,
        generatedTestCases: progress.generatedTestCases,
        statuses: progress.statuses,
        failureMessages: progress.failureMessages,
      };
    }

    await delay(BULK_JOB_POLL_INTERVAL_MS, signal);
  }
}

export async function runBulkTestCaseGeneration({
  projectId,
  requirements,
  batchSize,
  onProgress,
  signal,
}: RunBulkTestCaseGenerationInput): Promise<BulkGenerationOutcome> {
  const numericProjectId = Number(projectId);

  if (!Number.isInteger(numericProjectId) || numericProjectId <= 0) {
    throw new Error(`Invalid project ID: ${projectId}`);
  }

  if (requirements.length === 0) {
    throw new Error('Bulk generation requires at least one requirement.');
  }

  if (!Number.isInteger(batchSize) || batchSize <= 1) {
    throw new Error('Bulk generation requires a batch size greater than one.');
  }

  const existingJob = getActiveBulkGenerationJob(numericProjectId);

  const requirementThreadIds = requirements.map(
    requirement => requirement.requirementThreadId,
  );

  if (existingJob) {
    const sameSelection =
      existingJob.requirementThreadIds.length === requirementThreadIds.length &&
      existingJob.requirementThreadIds.every(id =>
        requirementThreadIds.includes(id),
      ) &&
      requirementThreadIds.every(id =>
        existingJob.requirementThreadIds.includes(id),
      );

    if (sameSelection) {
      return pollBulkGenerationJob(existingJob, onProgress, signal);
    }

    clearActiveBulkGenerationJob(numericProjectId);
  }

  const submission = await generateBulkTestCases(
    numericProjectId,
    requirementThreadIds,
    batchSize,
    signal,
  );

  const activeJob: ActiveBulkGenerationJob = {
    jobId: submission.jobId,
    projectId: numericProjectId,
    requirementThreadIds,
    batchSize,
  };

  saveActiveBulkGenerationJob(activeJob);

  onProgress?.({
    summary: {
      jobId: submission.jobId,
      status: submission.status,
      total: requirementThreadIds.length,
      processed: 0,
      succeeded: 0,
      failed: 0,
      pending: requirementThreadIds.length,
      persisted: 0,
      persistenceFailed: 0,
      errorMessage: '',
    },

    generatedTestCases: [],

    statuses: createBulkPendingStatuses(requirements),

    failureMessages: {},
  });

  return pollBulkGenerationJob(activeJob, onProgress, signal);
}

export async function resumeBulkTestCaseGeneration({
  projectId,
  onProgress,
  signal,
}: ResumeBulkTestCaseGenerationInput): Promise<
  BulkGenerationOutcome | undefined
> {
  const numericProjectId = Number(projectId);

  if (!Number.isInteger(numericProjectId) || numericProjectId <= 0) {
    return undefined;
  }

  const activeJob = getActiveBulkGenerationJob(numericProjectId);

  if (!activeJob) {
    return undefined;
  }

  return pollBulkGenerationJob(activeJob, onProgress, signal);
}
